import { intl, mathService } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import OpenInSourceLink from 'pages/requestList/components/OpenInSourceLink/OpenInSourceLink';
import { getActiveRequest } from 'pages/requestList/selectors/pageSelectors';
import { memo, useMemo } from 'react';

import { messages } from './QBooksJournalEntryFooter.messages';
import {
    AmountLabel,
    AmountValue,
    InfoTable,
    Separator,
    SeparatorContainer,
    StyledExchangeRate,
    TaxInfoTextWrapper,
    Wrapper,
} from './QBooksJournalEntryFooter.styles';
import { JournalEntryFooterProps } from './QBooksJournalEntryFooter.types';

const JournalEntryFooter = memo<JournalEntryFooterProps>((props) => {
    const {
        currency,
        lineAmountType,
        debitSubTotal,
        creditSubTotal,
        debitTaxTotal,
        creditTaxTotal,
        taxComponents,
        url,
        editUrl,
        isAUOrg,
    } = props;

    const request = useSelector(getActiveRequest);

    const { debitTotal, creditTotal } = useMemo(() => {
        if (lineAmountType === domain.LineAmountType.NoTax || lineAmountType === domain.LineAmountType.TaxInclusive) {
            return {
                debitTotal: debitSubTotal,
                creditTotal: creditSubTotal,
            };
        }

        return {
            debitTotal: mathService.add(debitSubTotal, debitTaxTotal),
            creditTotal: mathService.add(creditSubTotal, creditTaxTotal),
        };
    }, [lineAmountType, debitSubTotal, creditSubTotal, debitTaxTotal, creditTaxTotal]);

    const taxInfoText = useMemo(() => {
        if (!isAUOrg) {
            return null;
        }

        switch (lineAmountType) {
            case domain.LineAmountType.TaxInclusive:
                return messages.taxInclusiveText;

            case domain.LineAmountType.TaxExclusive:
                return messages.taxExclusiveText;

            default:
                return null;
        }
    }, [lineAmountType, isAUOrg]);

    const hasExchangeRate = Boolean(request.exchangeRate || request.currencyExchangeRate);
    const exchangeRate = request.isManualExchangeRate ? request.currencyExchangeRate : request.exchangeRate;

    return (
        <Wrapper>
            <div>
                <StyledExchangeRate request={request} />

                <OpenInSourceLink
                    url={url}
                    editUrl={editUrl}
                    statusV2={request.statusV2}
                    integrationType={request.integrationType}
                    integrationCode={request.integrationCode}
                />
            </div>

            <div>
                {taxInfoText && <TaxInfoTextWrapper>{taxInfoText}</TaxInfoTextWrapper>}

                <InfoTable>
                    <thead>
                        <tr>
                            <td />

                            <AmountLabel>{messages.debitTitle}</AmountLabel>

                            <AmountLabel>{messages.creditTitle}</AmountLabel>
                        </tr>
                    </thead>

                    <tbody>
                        {taxComponents.length > 0 && (
                            <tr>
                                <AmountLabel>{messages.subtotalTitle}</AmountLabel>

                                <AmountValue>{intl.formatNumber(debitSubTotal, 2)}</AmountValue>

                                <AmountValue>{intl.formatNumber(creditSubTotal, 2)}</AmountValue>
                            </tr>
                        )}

                        {taxComponents.map((tax) => (
                            <tr key={`${tax.taxCodeId}-${tax.taxRateId}`}>
                                <AmountLabel>{tax.taxCodeName}</AmountLabel>

                                <AmountValue>{intl.formatNumber(tax.taxDebitAmount, 2)}</AmountValue>

                                <AmountValue>{intl.formatNumber(tax.taxCreditAmount, 2)}</AmountValue>
                            </tr>
                        ))}

                        <tr>
                            <SeparatorContainer colSpan={3}>
                                <Separator />
                            </SeparatorContainer>
                        </tr>

                        <tr>
                            <AmountLabel>{`${messages.totalTitle} (${currency})`}</AmountLabel>

                            <AmountValue>{intl.formatNumber(debitTotal, 2)}</AmountValue>

                            <AmountValue>{intl.formatNumber(creditTotal, 2)}</AmountValue>
                        </tr>

                        {currency !== request.companyCurrency && hasExchangeRate && (
                            <tr>
                                <AmountLabel>{`${messages.totalTitle} (${request.companyCurrency})`}</AmountLabel>

                                <AmountValue>
                                    {intl.formatNumber(mathService.divide(debitTotal, exchangeRate || 1), 2)}
                                </AmountValue>

                                <AmountValue>
                                    {intl.formatNumber(mathService.divide(creditTotal, exchangeRate || 1), 2)}
                                </AmountValue>
                            </tr>
                        )}
                    </tbody>
                </InfoTable>
            </div>
        </Wrapper>
    );
});

export default JournalEntryFooter;
